import {
  Agreement,
  AgreementType,
  ApplicationProductTypeEnum,
  Query,
} from '@varo/graphql-schema-types';
import { router } from 'expo-router';
import rdcStrings from 'features/rdc/rdcStrings.en';
import CheckDepositDetailsHeader from 'features/rdc/screens/CheckDepositHistory/CheckDepositHistoryDetails/CheckDepositDetailsHeader';
import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import FetchAgreements from 'services/graphql/queries/GetAgreements';
import FetchCheckDetails from 'services/graphql/queries/Rdc';
import { ListItemLineFormat } from 'shared/components/vxl/ListItem/ListItemTextStack';
import {
  SectionedContentScreenModel,
  SectionedContentScreenSection,
} from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import { VXLComponent, VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { formatDisplayDate } from 'shared/utils/string/date';
import { Maybe } from 'shared/utils/types';
import { AnyVariables, CombinedError, useQuery } from 'urql';

export interface CheckDetailsResponse {
  title: string;
  sectionList: SectionedContentScreenModel;
}

type RDCDetailsTranslatedText = TFunction<'rdc', 'checkDetails'>;

interface CheckDetailsScreenState {
  loading: boolean;
  response?: Maybe<CheckDetailsResponse>;
  error?: CombinedError;
  refetch: () => void;
}

const formatCheckDetailsData = (
  checkDetailsData: Query,
  translatedText: RDCDetailsTranslatedText,
  accountAgreement: Maybe<Agreement>,
): CheckDetailsResponse => {
  const sections: SectionedContentScreenSection[] = [];

  if (checkDetailsData) {
    const rdc = checkDetailsData.rdc;

    const confirmationSection: VXLComponent[] = [];
    if (rdc?.rdcStatus) {
      confirmationSection.push({
        id: 'check-details-rdc-status',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: rdc.rdcStatus?.copy,
          showsChevron: false,
          secondaryCopy: translatedText(
            'labels.status',
            rdcStrings.checkDetails.labels.status,
          ),
          lineFormat: ListItemLineFormat.LABEL,
        },
      });
    }
    if (rdc?.submittedOn) {
      confirmationSection.push({
        id: 'check-details-rdc-submitted-on',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: formatDisplayDate(new Date(rdc.submittedOn)),
          secondaryCopy: translatedText(
            'labels.submittedOn',
            rdcStrings.checkDetails.labels.submittedOn,
          ),
          showsChevron: false,
          lineFormat: ListItemLineFormat.LABEL,
        },
      });
    }
    if (rdc?.confirmationNumber) {
      confirmationSection.push({
        id: 'check-details-rdc-confirmation-number',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: rdc.confirmationNumber,
          secondaryCopy: translatedText(
            'labels.confirmationNumber',
            rdcStrings.checkDetails.labels.confirmationNumber,
          ),
          showsChevron: false,
          lineFormat: ListItemLineFormat.LABEL,
        },
      });
    }

    if (rdc?.accountLink) {
      confirmationSection.push({
        id: 'check-details-account-link',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: rdc.accountLink?.name,
          secondaryCopy: translatedText(
            'labels.depositTo',
            rdcStrings.checkDetails.labels.depositTo,
          ),
          showsChevron: false,
          lineFormat: ListItemLineFormat.LABEL,
        },
      });
    }

    sections.push({
      data: confirmationSection,
    });

    if (rdc?.holds && rdc.holds.length > 0) {
      const availabilityScheduleSection: VXLComponent[] = [];

      availabilityScheduleSection.push({
        id: 'check-details-availability-Schedule',
        name: VXLComponentId.LIST_ITEM,
        props: {
          primaryCopy: formatDisplayDate(new Date(rdc.holds[0].endDate)),
          secondaryCopy: translatedText(
            'labels.availabilitySchedule',
            rdcStrings.checkDetails.labels.availabilitySchedule,
          ),
          trailingAccessory: rdc.holds[0].isActive
            ? {
                id: 'availability-Schedule-status',
                name: VXLComponentId.ICON,
                props: {
                  iconKey: 'STATUS_SUCCESS',
                  iconSize: 'MD',
                  iconColor: theme.color.status.positive,
                },
              }
            : undefined,
          showsChevron: false,
          lineFormat: ListItemLineFormat.LABEL,
        },
      });

      sections.push({
        data: availabilityScheduleSection,
      });
    }

    const imageSection: VXLComponent[] = [];
    if (rdc?.checkImageUrls) {
      imageSection.push({
        id: 'check-details-rdc-check-image-urls',
        name: VXLComponentId.CHECK_DEPOSIT_IMAGES_AND_AGREEMENT,
        props: {
          checkImageUrls: rdc.checkImageUrls,
          agreement: accountAgreement,
          hyperlinkPressed: () => {
            router.push({
              pathname: '/move-money/check-deposit/agreements',
            });
          },
        },
      });
    }

    sections.push({
      data: imageSection,
    });
  }
  const amountValueText =
    checkDetailsData?.rdc?.value.formattedWithPlusPrefix ?? '';
  return {
    title: translatedText('title'),
    sectionList: {
      header: {
        id: 'check-details-header',
        render: () => (
          <CheckDepositDetailsHeader amountValueText={amountValueText} />
        ),
      },
      sections: sections,
    },
  };
};

const useCheckDepositDetails = ({
  id,
}: {
  id: string;
}): CheckDetailsScreenState => {
  const { t: translatedText } = useTranslation('rdc', {
    keyPrefix: 'checkDetails',
  });
  const [{ data: checkDetailsData, fetching, error }, refetch] = useQuery<
    Query,
    AnyVariables
  >({
    query: FetchCheckDetails,
    requestPolicy: 'cache-and-network',
    variables: { id },
  });

  const [{ data: agreementData }] = useQuery<Query, AnyVariables>({
    query: FetchAgreements,
    requestPolicy: 'cache-first',
    variables: { types: ApplicationProductTypeEnum.Checking },
  });

  const accountAgreement = agreementData?.agreements.find(
    (a) => a.type === AgreementType.CheckingAccount,
  );

  const response = checkDetailsData
    ? formatCheckDetailsData(checkDetailsData, translatedText, accountAgreement)
    : null;

  return {
    loading: fetching,
    response,
    error,
    refetch,
  };
};

export default useCheckDepositDetails;
